// Translation helper utilities to improve Google Translate accuracy

// Add context attributes to help Google Translate understand content better
export const addTranslationContext = (element, context) => {
  if (!element) return;
  
  // Add data attributes to provide context to translation engines
  if (context.type) {
    element.setAttribute('data-translate-context', context.type);
  }
  
  if (context.domain) {
    element.setAttribute('data-translate-domain', context.domain);
  }
  
  if (context.preserve) {
    element.setAttribute('translate', 'no');
  }
};

// Common film industry terms that need context
export const filmIndustryTerms = {
  // Role types
  'Talent': 'Actor/Actress/Performer',
  'Crew': 'Film production crew member',
  'Director': 'Film director',
  'Producer': 'Film producer',
  'Cinematographer': 'Camera and lighting specialist',
  'Gaffer': 'Chief lighting technician',
  'Grip': 'Camera and lighting equipment specialist',
  
  // Technical terms
  'Casting': 'Actor selection process for film roles',
  'Audition': 'Actor tryout for role',
  'Principal Photography': 'Main filming period',
  'Post-Production': 'Film editing and finishing',
  'Distribution': 'Film release and marketing',
  'Screening': 'Film showing/presentation',
  
  // Business terms
  'Agency': 'Talent representation company',
  'Portfolio': 'Professional work samples',
  'Reel': 'Video showcase of work',
  'Headshot': 'Professional actor photograph',
  'Resume': 'Professional experience document',
  
  // Common platform terms
  'Profile': 'User account information',
  'Listing': 'Job posting or opportunity',
  'Networking': 'Professional connection building',
  'Community Hub': 'Professional group or specialization',
};

// Add glossary terms to the page for better translation context
export const injectTranslationGlossary = () => {
  const glossaryDiv = document.createElement('div');
  glossaryDiv.style.display = 'none';
  glossaryDiv.setAttribute('data-translate-glossary', 'true');
  
  // Add film industry context
  glossaryDiv.innerHTML = `
    <!-- Film Industry Context for Translation -->
    <span data-translate-context="film-role">Talent refers to actors, actresses, and performers in the film industry</span>
    <span data-translate-context="film-role">Crew refers to technical film production staff</span>
    <span data-translate-context="film-process">Casting is the process of selecting actors for film roles, not metal foundry work</span>
    <span data-translate-context="film-industry-french">In French film industry: Casting means "casting d'acteurs" or "sélection d'acteurs"</span>
    <span data-translate-context="film-business">Agency represents talent in the entertainment industry</span>
    <span data-translate-context="platform">Profile contains professional information for film industry networking</span>
    <span data-translate-context="geography">This platform focuses on the African film industry</span>
  `;
  
  document.body.appendChild(glossaryDiv);
};

// Mark elements that should not be translated
export const preserveFromTranslation = (selector) => {
  const elements = document.querySelectorAll(selector);
  elements.forEach(el => {
    el.setAttribute('translate', 'no');
    el.setAttribute('class', (el.getAttribute('class') || '') + ' notranslate');
  });
};

// Common selectors for content that shouldn't be translated
export const preserveSelectors = [
  'code',
  'pre', 
  '.code',
  '.logo',
  '.brand',
  '[data-no-translate]',
  '.currency',
  '.price',
  '.email',
  '.phone',
  '.url',
  '.username'
];


// Update page language context based on content
export const updateTranslationContext = (pageType, region = 'Africa') => {
  const contextMeta = document.querySelector('meta[name="translation-context"]');
  if (contextMeta) {
    contextMeta.setAttribute('content', `${pageType}, film industry, ${region}`);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'translation-context');
    meta.setAttribute('content', `${pageType}, film industry, ${region}`);
    document.head.appendChild(meta);
  }
};

// French translation overrides for specific terms
export const frenchTranslationOverrides = {
  'Register': 'S\'inscrire',
  'Home': 'Acceuil', 
  'Trailers': 'Bandes-Annonces',
  'Browse Trailers': 'Parcourir les Bandes-Annonces'
};

// Apply French translation overrides after Google Translate processes the page
export const applyFrenchOverrides = () => {
  // Wait for Google Translate to finish processing
  setTimeout(() => {
    const elements = document.querySelectorAll('[data-translate-fr]');
    elements.forEach(element => {
      const frenchText = element.getAttribute('data-translate-fr');
      const currentLang = document.documentElement.lang || 
                         (window.google && window.google.translate && window.google.translate.TranslateElement && 
                          window.google.translate.TranslateElement.getSelection && 
                          window.google.translate.TranslateElement.getSelection().targetLanguage);
      
      if (currentLang === 'fr' && frenchText) {
        // Only override if we're in French mode
        if (element.textContent !== frenchText) {
          element.textContent = frenchText;
        }
      }
    });
  }, 1000);
};

// Mark personal names to never be translated
export const preservePersonalNames = () => {
  // Look for elements that likely contain personal names
  const nameSelectors = [
    '.notranslate',
    '[translate="no"]',
    '[data-name]',
    '.profile-name',
    '.user-name',
    '.display-name'
  ];
  
  nameSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(el => {
      el.setAttribute('translate', 'no');
      el.classList.add('notranslate');
    });
  });
};

// Enhanced initialization for translation helpers
export const initTranslationHelpers = () => {
  // Add glossary for context
  injectTranslationGlossary();
  
  // Preserve elements that shouldn't be translated
  preserveSelectors.forEach(selector => {
    preserveFromTranslation(selector);
  });
  
  // Preserve personal names
  preservePersonalNames();
  
  // Add language hints to main content areas
  const mainContent = document.querySelector('#root');
  if (mainContent) {
    mainContent.setAttribute('lang', 'en');
  }
  
  // Set up observer for Google Translate changes
  const observer = new MutationObserver((mutations) => {
    let shouldApplyOverrides = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
        shouldApplyOverrides = true;
      }
      if (mutation.type === 'childList') {
        // Check if Google Translate has modified the DOM
        const addedNodes = Array.from(mutation.addedNodes);
        if (addedNodes.some(node => node.nodeType === 1 && node.classList && node.classList.contains('goog-te-spinbox'))) {
          shouldApplyOverrides = true;
        }
      }
    });
    
    if (shouldApplyOverrides) {
      setTimeout(() => {
        applyFrenchOverrides();
        preservePersonalNames();
      }, 500);
    }
  });
  
  observer.observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true
  });
};

export const disableTranslationTemporarily = () => {
    document.documentElement.setAttribute('translate', 'no');
};

export const enableTranslation = () => {
    document.documentElement.removeAttribute('translate');
};

// Optional: manually trigger Google Translate to reapply translation
export const retranslatePage = () => {
    const frame = document.querySelector('.goog-te-menu-frame');
    if (!frame) return;
    const current = frame.contentDocument.querySelector('.goog-te-menu2-item-selected');
    if (current) current.click();
};
